import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useCart } from '../contexts/CartContext';
import { 
  ShoppingBag, 
  ShoppingCart,
  ClipboardList,
  LogOut,
  Menu,
  X,
  FileSearch,
  HelpCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const navItems = [
  { path: '/customer/catalog', label: '商品カタログ', icon: ShoppingBag },
  { path: '/customer/cart', label: 'カート', icon: ShoppingCart, showBadge: true },
  { path: '/customer/orders', label: '注文履歴', icon: ClipboardList },
  { path: '/customer/help', label: '使い方', icon: HelpCircle },
];

export const CustomerLayout = ({ children }) => {
  const { user, logout } = useCustomerAuth();
  const { items } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 顧客画面のタイトルを設定
  useEffect(() => {
    document.title = 'やさまるFish';
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
  };

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-layout">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/customer/catalog" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <ShoppingBag className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg text-slate-900 hidden sm:block">やさまるFish</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                      isActive 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                    data-testid={`nav-${item.path.split('/').pop()}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                    {item.showBadge && cartItemCount > 0 && (
                      <Badge className="bg-blue-600 text-white ml-1">{cartItemCount}</Badge>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User Info & Logout */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user?.customer_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-slate-600 hover:text-red-600"
                data-testid="customer-logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="flex md:hidden items-center gap-2">
              <Link to="/customer/cart" className="relative p-2">
                <ShoppingCart className="h-6 w-6 text-slate-600" />
                {cartItemCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs h-5 w-5 flex items-center justify-center p-0">
                    {cartItemCount}
                  </Badge>
                )}
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md ${
                      isActive 
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-slate-600'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                    {item.showBadge && cartItemCount > 0 && (
                      <Badge className="bg-blue-600 text-white ml-auto">{cartItemCount}</Badge>
                    )}
                  </Link>
                );
              })}
              <div className="border-t border-slate-200 pt-3 mt-3">
                <div className="px-3 py-2">
                  <p className="font-medium text-slate-900">{user?.customer_name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  ログアウト
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
